import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { getCenterScopedUrl } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";
const API = "/api/placed-impact-assessment";
const CACHE_KEY = "placedImpactAssessment";
const CHUNK_SIZE = 500;
const PARALLEL = 2;

const COLUMNS = [
    { key: "beneficiaryId", label: "Beneficiary ID" },
    { key: "location", label: "Location" },
    { key: "batchName", label: "Batch Name" },
    { key: "batchId", label: "Batch ID" },
    { key: "uniqueTraineeId", label: "Unique Trainee ID" },
    { key: "name", label: "Name" },
    { key: "mobileNumber", label: "Mobile Number" },
    { key: "salutation", label: "Salutation" },
    { key: "nameOfTrainee", label: "Name of Trainee" },
    { key: "dateOfBirth", label: "Date of Birth" },
    { key: "gender", label: "Gender (Male, Female, Transgender)" },
    { key: "traineeMobileNumber", label: "Trainee Mobile Number" },
    { key: "guardianContactNumber", label: "Guardian Contact Number" },
    { key: "emailId", label: "Email ID" },
    { key: "traineeAddress", label: "Trainee Address" },
    { key: "district", label: "District" },
    { key: "state", label: "State" },
    { key: "batchEndDate", label: "Batch End Date (DD/MM/YYYY)" },
    { key: "placementStatus", label: "Placement Status (Yes/No)" },
    { key: "dateOfPlacement", label: "Date of Placement (DD/MM/YYYY)" },
    { key: "employerName", label: "Employer Name / Self-Employed" },
    { key: "nameOfPointPerson", label: "Name of Point Person from the Company/Employer" },
    { key: "contactNoOfPointPerson", label: "Contact No. of Point Person" },
    { key: "emailIdOfPointPerson", label: "Email Id of Point Person" },
    { key: "designationOfPointPerson", label: "Designation of Point Person" },
    { key: "designationOfTrainee", label: "Designation of Trainee" },
    { key: "incomePerMonth", label: "Income Per Month" },
    { key: "offerLetterCollected", label: "Offer Letter Collected (Yes/No)" },
    { key: "employerAddress", label: "Employer Address" },
    { key: "districtOfPlacement", label: "District of Placement" },
    { key: "stateOfPlacement", label: "State of Placement" },
    { key: "callStatus", label: "Call Status" },
    { key: "respondent", label: "Respondent" },
    { key: "alternateMobileNumber", label: "Alternate Mobile Number" },
    { key: "callRescheduleDate", label: "Call Reschedule Date (DD/MM/YYYY)" },
    { key: "currentlyEmployed", label: "Are you currently employed?" },
    { key: "specifyOthers", label: "Please specify others" },
    { key: "lastWorkDate", label: "When did you last work?" },
    { key: "reasonForLeavingJob", label: "Reason for leaving the previous job?" },
    { key: "activelyLookingForJob", label: "Are you actively looking for a new job?" },
    { key: "companyOrganizationName", label: "Company / Organization Name" },
    { key: "jobRoleDesignation", label: "Job Role / Designation" },
    { key: "jobLocationDistrict", label: "Job Location (District)" },
    { key: "jobLocationCityState", label: "Job Location (City, State)" },
    { key: "typeOfEmployment", label: "Type of Employment" },
    { key: "currentSalary", label: "What is your current salary or earning per month?" },
    { key: "promotionsOrRoleChanges", label: "Have you received any promotions, increments, or role changes since joining?" },
    { key: "specifyPromotion", label: "If yes, please specify Promotion" },
    { key: "specifyRoleChange", label: "If yes, please specify Role change" },
    { key: "specifyIncrement", label: "If yes, please specify Increment" },
    { key: "trainingHelpedGetJob", label: "Do you feel the training helped you get this job or improve your career prospects?" },
    { key: "trainingNotUseful", label: "What makes the training not useful according to you?" },
    { key: "confidenceInJobTasks", label: "How confident do you feel in performing your job tasks after the training?" },
    { key: "satisfiedWithJob", label: "How satisfied are you with your current job?" },
    { key: "reasonForDissatisfaction", label: "Reason for Dissatisfaction" },
    { key: "recommendTrainingProgram", label: "Would you recommend this training program to others?" },
    { key: "suggestionsForFutureTraining", label: "What improvements would you suggest for future training programs?" },
    { key: "newOfferLetterCollected", label: "New Offer Letter Collected" },
    { key: "newSalarySlipCollected", label: "New Salary Slip Collected" },
    { key: "remarks", label: "Remarks" },
    { key: "rocMemberName", label: "ROC Member Name" },
    { key: "date", label: "Date" },
];

const emptyForm = Object.fromEntries(COLUMNS.map(c => [c.key, ""]));

export default function PlacedImpactAssessment() {
    const token = localStorage.getItem("token");
    const store = useDataStore();

    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => setMsg({ type, text });

    const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
    const jsonHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);

    const safeJson = async (res) => {
        const text = await res.text();
        try { return text ? JSON.parse(text) : null; } catch { return text; }
    };

    /* ── EXCEL UPLOAD ── */

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [exporting, setExporting] = useState(false);

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select an Excel file");

        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            dataRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }

        if (dataRows.length < 2) return flash("err", "No data rows found");

        const dataOnly = dataRows.slice(1).filter(r => r.some(v => String(v).trim() !== ""));
        const totalRows = dataOnly.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);

        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        flash("", "");

        let totalInserted = 0, totalUpdated = 0, totalSkipped = 0, failed = false, firstError = "";

        const uploadOneChunk = async (i) => {
            const chunk = dataOnly.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const res = await fetch(`${BASE_URL}${API}/upload-json`, {
                method: "POST",
                headers: jsonHeaders,
                body: JSON.stringify(chunk),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const data = await safeJson(res);
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, updated: data?.updated ?? 0, skipped: data?.skipped ?? 0 };
        };

        let uploadedSoFar = 0;
        for (let start = 0; start < totalChunks && !failed; start += PARALLEL) {
            const indices = Array.from({ length: Math.min(PARALLEL, totalChunks - start) }, (_, k) => start + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks });

            const results = await Promise.allSettled(indices.map(uploadOneChunk));
            for (const r of results) {
                if (r.status === "fulfilled") {
                    totalInserted += r.value.inserted;
                    totalUpdated += r.value.updated;
                    totalSkipped += r.value.skipped;
                    uploadedSoFar += r.value.inserted + r.value.updated + r.value.skipped;
                } else {
                    failed = true;
                    firstError = r.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: start + indices.length, totalChunks, error: failed });
        }

        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Updated: ${totalUpdated.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
            setFile(null);
            setUploadProgress(null);
            store.clearCache(CACHE_KEY);
            fetchFirst100();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ── TEMPLATE ── */

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([COLUMNS.map(c => c.label)]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "placed_impact_assessment_template.xlsx");
    };

    /* ── FETCH ── */

    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const scopedUrl = getCenterScopedUrl(BASE_URL, API);
            const sep = scopedUrl.includes("?") ? "&" : "?";
            const res = await fetch(`${scopedUrl}${sep}page=0&size=100`, { headers: authHeaders });
            const data = await safeJson(res);
            const list = Array.isArray(data) ? data : (data?.content ?? []);
            setRows(list);
            store.setRows(CACHE_KEY, list, false);
            setAllLoaded(false);
        } catch {
            setRows([]);
            flash("err", "Failed to load records");
        } finally {
            setLoading(false);
        }
    };

    const fetchAllRows = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, API), { headers: authHeaders });
            const data = await safeJson(res);
            const list = Array.isArray(data) ? data : (data?.content ?? []);
            setRows(list);
            setVisibleCount(list.length);
            store.setRows(CACHE_KEY, list, true);
            setAllLoaded(true);
        } catch {
            flash("err", "Failed to load all records");
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => { fetchFirst100(); }, []);

    /* ── SEARCH ── */

    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return rows;
        return rows.filter(r => COLUMNS.some(c => String(r[c.key] ?? "").toLowerCase().includes(q)));
    }, [rows, search]);

    /* ── EXPORT ── */

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let exportRows = rows;
            if (!allLoaded) {
                const res = await fetch(getCenterScopedUrl(BASE_URL, API), { headers: authHeaders });
                const data = await safeJson(res);
                exportRows = Array.isArray(data) ? data : (data?.content ?? []);
            }
            const wsData = [
                COLUMNS.map(c => c.label),
                ...exportRows.map(r => COLUMNS.map(c => r[c.key] ?? "")),
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), "Placed Impact Assessment");
            XLSX.writeFile(wb, "placed_impact_assessment.xlsx");
        } catch (err) {
            flash("err", err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    /* ── DELETE ── */

    const deleteRecord = async (id) => {
        if (!window.confirm("Delete this record?")) return;
        const res = await fetch(`${BASE_URL}${API}/${id}`, { method: "DELETE", headers: authHeaders });
        if (res.ok) {
            flash("ok", "Deleted");
            store.clearCache(CACHE_KEY);
            fetchAllRows();
        } else {
            flash("err", "Delete failed");
        }
    };

    /* ── MODAL (Add / Edit) ── */

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id);
        setForm(Object.fromEntries(COLUMNS.map(c => [c.key, row[c.key] ?? ""])));
        setModalOpen(true);
    };

    const saveRecord = async () => {
        if (!form.beneficiaryId?.trim()) return flash("err", "Beneficiary ID is required");
        setSaving(true);
        try {
            const url = editingId ? `${BASE_URL}${API}/${editingId}` : `${BASE_URL}${API}`;
            const method = editingId ? "PUT" : "POST";
            const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(form) });
            if (!res.ok) { flash("err", "Save failed"); return; }
            flash("ok", editingId ? "Record updated" : "Record created");
            setModalOpen(false);
            store.clearCache(CACHE_KEY);
            fetchAllRows();
        } catch {
            flash("err", "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const colSpan = COLUMNS.length + 2;

    /* ── RENDER ── */

    return (
        <div className="pia-page">

            <div className="header">
                <h1>Placed Impact Assessment</h1>
                {/* <button className="btn btn-primary" onClick={openCreate}>+ Add Record</button> */}
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {/* Upload card */}
            <div className="card" style={{ marginBottom: 14 }}>
                <div className="grid-4">
                    <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} />
                    <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading || !file}>
                        {uploading ? "Uploading..." : "Upload Excel"}
                    </button>
                    <button className="btn btn-outline" onClick={downloadTemplate}>
                        Download Excel Template
                    </button>
                    <button className="btn btn-outline" onClick={exportExcel} disabled={exporting}>
                        {exporting ? "Processing..." : "Export Excel"}
                    </button>
                </div>
            </div>

            {uploadProgress && (
                <div className="card" style={{ padding: "10px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                        <span>
                            {uploadProgress.error
                                ? "Upload failed"
                                : uploadProgress.chunk >= uploadProgress.totalChunks
                                    ? "Upload complete"
                                    : `Uploading chunk ${uploadProgress.chunk} / ${uploadProgress.totalChunks}…`}
                        </span>
                        <span>{uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows</span>
                    </div>
                    <div style={{ background: "#e5e7eb", borderRadius: 8, height: 8, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            borderRadius: 8,
                            background: uploadProgress.error ? "#ef4444" : "#2563eb",
                            width: `${uploadProgress.total ? Math.round((uploadProgress.uploaded / uploadProgress.total) * 100) : 0}%`,
                            transition: "width 0.3s"
                        }} />
                    </div>
                </div>
            )}

            {/* Table card */}
            <div className="card">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search by beneficiary ID, name, batch, employer..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    {!allLoaded && (
                        <button
                            className="btn btn-outline"
                            onClick={fetchAllRows}
                            disabled={loadingAll}
                            style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        >
                            {loadingAll ? "Loading…" : `Load All Data (${rows.length} loaded)`}
                        </button>
                    )}
                </div>

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                {COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                                {/* <th className="actions">Actions</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, visibleCount).map((r, i) => (
                                <tr key={r.id}>
                                    <td>{i + 1}</td>
                                    {COLUMNS.map(c => <td key={c.key}>{r[c.key] ?? ""}</td>)}
                                    {/* <td className="actions">
                                        <div style={{ display: "flex", gap: 4 }}>
                                            <button className="btn btn-sm btn-outline" onClick={() => openEdit(r)}>Edit</button>
                                            <button className="btn btn-sm btn-danger" onClick={() => deleteRecord(r.id)}>Delete</button>
                                        </div>
                                    </td> */}
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan={colSpan} className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}

                            {!allLoaded && filtered.length > 0 && (
                                <tr>
                                    <td colSpan={colSpan} style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllRows} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan={colSpan} style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={() => setVisibleCount(v => v + 500)}>
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add / Edit Modal */}
            {modalOpen && createPortal(
                <div className="modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="ep-modal wide" onClick={e => e.stopPropagation()}>
                        <h2>{editingId ? "Edit Record" : "Add Record"}</h2>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 16 }}>
                            {COLUMNS.map(c => (
                                <div key={c.key}>
                                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                                        {c.label}{c.key === "beneficiaryId" ? " *" : ""}
                                    </label>
                                    <input
                                        style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13 }}
                                        value={form[c.key]}
                                        onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button className="btn" onClick={() => setModalOpen(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={saveRecord} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <style>{`
.pia-page{padding:22px;background:#f7f7fb;min-height:100vh;font-family:Inter}
.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
h1{font-size:26px;font-weight:900;margin:0}
.flash{padding:10px;border-radius:10px;margin:10px 0;font-weight:700}
.flash-ok{background:#ecfdf5;color:#065f46}
.flash-err{background:#fef2f2;color:#991b1b}
.card{background:#fff;border:1px solid #e5e7eb;border-radius:14px;padding:16px;margin-bottom:14px}
.grid-4{display:grid;grid-template-columns:1fr auto auto auto;gap:10px;align-items:center}
.btn{border:1px solid #e5e7eb;padding:10px 14px;border-radius:12px;font-weight:800;cursor:pointer;background:#fff;font-size:13px}
.btn:disabled{opacity:0.5;cursor:not-allowed}
.btn-primary{background:#2563eb;color:#fff;border-color:#2563eb}
.btn-outline{border-color:#d1d5db;color:#374151}
.btn-sm{padding:6px 10px;font-size:12px}
.btn-danger{color:#991b1b;border-color:#fca5a5}
.search{margin-bottom:10px;padding:10px;border-radius:12px;border:1px solid #e5e7eb;font-size:13px;outline:none;width:100%;box-sizing:border-box}
.table-wrap{overflow-x:auto}
.ep-table{width:100%;min-width:8000px;border-collapse:collapse}
th,td{padding:10px;border-bottom:1px solid #e5e7eb;font-size:13px;white-space:nowrap}
th{background:#fcfcff;text-align:left;font-weight:700}
td.actions{position:sticky;right:0;background:#fff}
.muted{color:#6b7280;font-weight:700;text-align:center;padding:20px}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:99999}
.ep-modal{background:#fff;padding:24px;border-radius:16px;width:800px;max-height:90vh;overflow:auto}
.ep-modal h2{margin:0 0 16px;font-size:18px;font-weight:900}
.ep-modal.wide{width:960px}
`}</style>
        </div>
    );
}
